import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Activity, Shield, AlertCircle, CheckCircle2, Zap, Users } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/governance/metrics`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const executionData = [
    { name: 'Mon', executions: 45 },
    { name: 'Tue', executions: 52 },
    { name: 'Wed', executions: 48 },
    { name: 'Thu', executions: 61 },
    { name: 'Fri', executions: 55 },
    { name: 'Sat', executions: 38 },
    { name: 'Sun', executions: 42 },
  ];

  const aiHealthData = [
    { name: 'Bias', value: metrics?.bias_score || 5.2, color: '#10b981' },
    { name: 'Transparency', value: metrics?.transparency_score || 92, color: '#8b5cf6' },
    { name: 'Accountability', value: metrics?.accountability_score || 88, color: '#6366f1' },
    { name: 'Security', value: metrics?.security_score || 95.5, color: '#06b6d4' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Welcome to AgentOS
        </h1>
        <p className="text-sm sm:text-base text-gray-400">Enterprise AI Agent Operating System - Unified Control Plane</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="metric-card" data-testid="metric-total-agents">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-violet-400" />
            </div>
            <div className="flex items-center gap-1 text-green-400 text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>+12%</span>
            </div>
          </div>
          <h3 className="text-3xl font-bold text-white mb-1">{metrics?.total_agents || 0}</h3>
          <p className="text-gray-400 text-sm">Total Agents</p>
          <p className="text-green-400 text-xs mt-2">{metrics?.active_agents || 0} Active</p>
        </div>

        <div className="metric-card" data-testid="metric-executions">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center">
              <Activity className="w-6 h-6 text-indigo-400" />
            </div>
            <div className="flex items-center gap-1 text-green-400 text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>+8%</span>
            </div>
          </div>
          <h3 className="text-3xl font-bold text-white mb-1">{metrics?.total_executions || 0}</h3>
          <p className="text-gray-400 text-sm">Total Executions</p>
          <p className="text-yellow-400 text-xs mt-2">{metrics?.failed_executions || 0} Failed</p>
        </div>

        <div className="metric-card" data-testid="metric-security">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-cyan-400" />
            </div>
            <CheckCircle2 className="w-5 h-5 text-green-400" />
          </div>
          <h3 className="text-3xl font-bold text-white mb-1">{metrics?.security_score || 95.5}%</h3>
          <p className="text-gray-400 text-sm">Security Score</p>
          <p className="text-green-400 text-xs mt-2">{metrics?.compliance_status || 'Compliant'}</p>
        </div>

        <div className="metric-card" data-testid="metric-response-time">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-pink-400" />
            </div>
            <div className="flex items-center gap-1 text-red-400 text-sm">
              <TrendingDown className="w-4 h-4" />
              <span>-15%</span>
            </div>
          </div>
          <h3 className="text-3xl font-bold text-white mb-1">{metrics?.avg_response_time || 234}ms</h3>
          <p className="text-gray-400 text-sm">Avg Response Time</p>
          <p className="text-green-400 text-xs mt-2">Faster than usual</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Execution Trends */}
        <div className="glass-panel p-4 sm:p-6" data-testid="execution-chart">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Execution Trends</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={executionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  background: 'rgba(0, 0, 0, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              />
              <Line type="monotone" dataKey="executions" stroke="#8b5cf6" strokeWidth={3} dot={{ fill: '#8b5cf6' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* AI Health Metrics */}
        <div className="glass-panel p-4 sm:p-6" data-testid="health-chart">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">AI Health Metrics</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={aiHealthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  background: 'rgba(0, 0, 0, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                }}
              />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {aiHealthData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Alerts & Warnings */}
      <div className="glass-panel p-4 sm:p-6" data-testid="alerts-section">
        <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Recent Alerts</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Policy Violation Detected</p>
              <p className="text-gray-400 text-sm">Agent "DataProcessor-42" exceeded rate limit - 2 minutes ago</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-white font-medium">Compliance Check Passed</p>
              <p className="text-gray-400 text-sm">All agents passed SOC 2 compliance audit - 1 hour ago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;